export const GUAYAQUIL_TIME_ZONE = 'America/Guayaquil';

export interface LocalSchedule {
  date: string;
  time: string;
}

interface DateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function zonedParts(date: Date, timeZone: string): DateTimeParts {
  const values = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, Number(part.value)]),
  );
  return values as unknown as DateTimeParts;
}

function offsetMinutes(date: Date, timeZone: string): number {
  const parts = zonedParts(date, timeZone);
  const representedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  return Math.round((representedAsUtc - date.getTime()) / 60_000);
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function parseLocal(date: string, time: string): DateTimeParts {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dateMatch || !timeMatch) throw new Error('invalid_date');
  const parts = {
    year: Number(dateMatch[1]),
    month: Number(dateMatch[2]),
    day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]),
    minute: Number(timeMatch[2]),
    second: 0,
  };
  const validation = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
  if (
    validation.getUTCFullYear() !== parts.year
    || validation.getUTCMonth() + 1 !== parts.month
    || validation.getUTCDate() !== parts.day
    || parts.hour > 23
    || parts.minute > 59
  ) {
    throw new Error('invalid_date');
  }
  return parts;
}

function instantForLocal(parts: DateTimeParts, timeZone: string): Date {
  const localAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );
  let candidate = new Date(localAsUtc);
  for (let pass = 0; pass < 2; pass += 1) {
    candidate = new Date(localAsUtc - offsetMinutes(candidate, timeZone) * 60_000);
  }
  const resolved = zonedParts(candidate, timeZone);
  if (Object.keys(parts).some((key) =>
    resolved[key as keyof DateTimeParts] !== parts[key as keyof DateTimeParts])) {
    throw new Error('invalid_date');
  }
  return candidate;
}

export function buildGuayaquilDateTime(
  date: string,
  time: string,
  now = new Date(),
): string {
  const parts = parseLocal(date, time);
  const instant = instantForLocal(parts, GUAYAQUIL_TIME_ZONE);
  if (instant.getTime() <= now.getTime()) throw new Error('past_date');
  const offset = offsetMinutes(instant, GUAYAQUIL_TIME_ZONE);
  const sign = offset >= 0 ? '+' : '-';
  const absoluteOffset = Math.abs(offset);
  return `${date}T${time}:00${sign}${pad(Math.floor(absoluteOffset / 60))}:${pad(absoluteOffset % 60)}`;
}

export function defaultGuayaquilSchedule(now = new Date()): LocalSchedule {
  const future = new Date(now.getTime() + 30 * 60_000);
  const parts = zonedParts(future, GUAYAQUIL_TIME_ZONE);
  const roundedMinutes = Math.ceil(parts.minute / 30) * 30;
  const localAsUtc = new Date(Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    roundedMinutes,
  ));
  return {
    date: `${localAsUtc.getUTCFullYear()}-${pad(localAsUtc.getUTCMonth() + 1)}-${pad(localAsUtc.getUTCDate())}`,
    time: `${pad(localAsUtc.getUTCHours())}:${pad(localAsUtc.getUTCMinutes())}`,
  };
}
